import { useState } from "react";
import { AdminLayout } from "./Admin";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Loader2, Play, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

type Check = { name: string; ok: boolean; detail?: string };
type Report = { ok: boolean; total: number; passed: number; failed: number; checks: Check[]; booking_id: string; cleaned_up: boolean };

const AdminE2ETest = () => {
  const [running, setRunning] = useState(false);
  const [cleanup, setCleanup] = useState(true);
  const [report, setReport] = useState<Report | null>(null);

  const run = async () => {
    setRunning(true); setReport(null);
    try {
      const { data, error } = await supabase.functions.invoke("e2e-test-suite", { body: { cleanup } });
      if (error || (data as any)?.error) throw new Error(error?.message || (data as any)?.error);
      setReport(data as Report);
      (data as Report).ok ? toast.success(`All ${(data as Report).total} checks passed`) :
        toast.error(`${(data as Report).failed} of ${(data as Report).total} checks failed`);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <AdminLayout title="E2E TEST SUITE">
      <p className="font-body text-muted-foreground mb-6 max-w-2xl">
        Runs a complete booking → payment → refund flow end-to-end and checks that
        availability, reconciliation, stock, and slot-lock state all reconcile correctly.
        Open the customer-facing calendar in another tab before running to watch realtime updates.
      </p>

      <div className="bg-panel border border-border p-6 mb-6 flex flex-wrap items-center gap-4">
        <Button onClick={run} disabled={running} className="gap-2">
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          Run full suite
        </Button>
        <div className="flex items-center gap-2 text-sm">
          <Switch checked={cleanup} onCheckedChange={setCleanup} />
          <span className="font-body">Clean up synthetic booking after run</span>
        </div>
        {report && (
          <div className="ml-auto flex gap-2">
            <Badge variant={report.ok ? "default" : "destructive"}>
              {report.passed}/{report.total} passed
            </Badge>
            {report.booking_id && <Badge variant="outline" className="font-mono text-xs">{report.booking_id.slice(0, 8)}</Badge>}
          </div>
        )}
      </div>

      {report && (
        <div className="bg-panel border border-border p-6">
          <h3 className="font-display tracking-widest text-sm mb-4">CHECKS</h3>
          <ul className="space-y-3">
            {report.checks.map((c, i) => (
              <li key={i} className="flex gap-3 items-start text-sm font-body">
                {c.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5" /> : <XCircle className="w-4 h-4 text-destructive mt-0.5" />}
                <div>
                  <p>{c.name}</p>
                  {c.detail && <p className="text-xs text-muted-foreground font-mono mt-0.5">{c.detail}</p>}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminE2ETest;